import React, { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import muiTheme from '../../config/muiTheme'
import {
	EuiButton,
	EuiButtonIcon,
	EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiLoadingSpinner,
	EuiPageHeader,
	EuiText,
	EuiTitle,
} from '@equipmentshare/ds2'
import { CloseLineIcon } from '@equipmentshare/ds2-icons'
import { heapEvent } from '@equipmentshare/heaper'
import { InventoryLocation } from 'inventory-web'
import { DemandRequestTargetType, Part, Permission, ReservationTargetType, useCreateManualTransferOrderMutation, useCompleteDemandRequestLineItemsMutation, useBulkUpdateDemandRequestLineItemFulfillerNotesMutation } from '../../graphql'
import { LineItem } from '../../types/demandRequest'
import useTransferOrderForm, { TransferOrderInitialValues, TransferOrderLineItem } from './hooks/useTransferOrderForm'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import InventoryLocationPicker from '../../components/InventoryLocationPicker'
import PermissionsCheck from '../../components/PermissionsCheck'
import TransferOrderLineItemsList from './components/TransferOrderLineItemsList'
import { LAYOUT_CONSTANTS, Z_INDEX } from '../requests/constants/layoutConstants'

type CreateTransferOrderState = {
	fromInventoryId?: number,
	toInventoryId?: number,
	selectedLineItems?: LineItem[],
	requestId?: string,
	requestNumber?: number,
}

function mapRequestTargetType( targetType: DemandRequestTargetType | null | undefined ): ReservationTargetType | '' {
	switch ( targetType ) {
		case DemandRequestTargetType.WorkOrder: return ReservationTargetType.WorkOrder
		case DemandRequestTargetType.Invoice: return ReservationTargetType.Invoice
		case DemandRequestTargetType.Order: return ReservationTargetType.Order
		default: return ''
	}
}

function combineLineItemNotes( lineNotes: string | null | undefined, fulfillerNotes: string | null | undefined ): string {
	const parts = [ lineNotes?.trim(), fulfillerNotes?.trim() ].filter( ( v ): v is string => Boolean( v ) )
	return parts.join( ', ' )
}

function buildInitialValues( state: CreateTransferOrderState | null ): TransferOrderInitialValues | undefined {
	if ( !state?.fromInventoryId && !state?.toInventoryId && !state?.selectedLineItems?.length ) {
		return undefined
	}
	const lineItems: TransferOrderLineItem[] | undefined = state.selectedLineItems?.map( ( item ) => ( {
		id: `line-item-${Date.now()}-${Math.random().toString( 36 ).substr( 2, 9 )}`,
		productId: String( item.productId ),
		quantityRequested: item.quantityRequested,
		allocationTargetType: mapRequestTargetType( item.targetType ),
		allocationTargetId: item.targetId ?? '',
		lineNote: combineLineItemNotes( item.lineNotes, item.fulfillerNotes ),
		demandRequestLineItemId: item.lineItemId,
		selectedPart: item.product as unknown as Part,
	} ) )
	return {
		fromStoreId: state.fromInventoryId ? String( state.fromInventoryId ) : undefined,
		toStoreId: state.toInventoryId ? String( state.toInventoryId ) : undefined,
		requesterNote: state.requestNumber ? `REQ-${state.requestNumber}` : undefined,
		lineItems,
	}
}

export default function CreateTransferOrder() {
	const navigate = useNavigate()
	const location = useLocation()
	const locationState = ( location.state as CreateTransferOrderState | null ) ?? null

	const initialValues = buildInitialValues( locationState )
	const form = useTransferOrderForm( initialValues )

	const [ createManualTransferOrder, { loading: mutationLoading, error: mutationError } ] = useCreateManualTransferOrderMutation()
	const [ completeLineItems ] = useCompleteDemandRequestLineItemsMutation()
	const [ bulkUpdateFulfillerNotes ] = useBulkUpdateDemandRequestLineItemFulfillerNotesMutation()

	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const [ isLoading, setIsLoading ] = useState( false )
	const [ hasError, setHasError ] = useState( false )
	const [ errorMessage, setErrorMessage ] = useState( '' )
	const [ showValidationErrors, setShowValidationErrors ] = useState( false )
	const [ isErrorPanelDismissed, setIsErrorPanelDismissed ] = useState( false )

	const [ selectedFromStore, setSelectedFromStore ] = useState<InventoryLocation>()
	const [ selectedToStore, setSelectedToStore ] = useState<InventoryLocation>()

	const handleFromStoreChange = ( id: number, inventory?: InventoryLocation ) => {
		const idString = id ? id.toString() : ''
		form.setFromStoreId( idString )
		setSelectedFromStore( inventory )
	}

	const handleToStoreChange = ( id: number, inventory?: InventoryLocation ) => {
		const idString = id ? id.toString() : ''
		form.setToStoreId( idString )
		setSelectedToStore( inventory )
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Transfer Orders - Create - Page - Click - Cancel', {} )
		form.resetForm()
		setShowValidationErrors( false )
		navigate( locationState?.requestId ? `/requests/${locationState.requestId}` : '/transfer-orders' )
	}

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Transfer Orders - Create - Page - Click - Submit', {} )

		setShowValidationErrors( true )
		setIsErrorPanelDismissed( false )

		if ( !form.validateForm() ) {
			setHasError( true )
			setErrorMessage( 'Please correct the errors in the form before submitting.' )
			return
		}

		setIsLoading( true )
		setHasError( false )
		setErrorMessage( '' )

		try {
			const input = {
				fromStoreId: form.formData.fromStoreId,
				toStoreId: form.formData.toStoreId,
				requesterNote: form.formData.requesterNote.trim() || null,
				lineItems: form.formData.lineItems
					.map( ( li ) => ( {
						productId: li.productId,
						quantityRequested: li.quantityRequested,
						allocationTargetType: li.allocationTargetType ? li.allocationTargetType as ReservationTargetType : null,
						allocationTargetId: li.allocationTargetId ? parseInt( li.allocationTargetId, 10 ) : null,
						lineNote: li.lineNote?.trim() || null,
						demandRequestLineItemId: li.demandRequestLineItemId ?? null,
					} ) ),
			}

			const result = await createManualTransferOrder( { variables: { input } } )
			const response = result.data?.createManualTransferOrder

			if ( response?.success ) {
				const successMessage = response.message || 'Transfer order created successfully'
				showSuccess( `transferOrderSuccess${Date.now()}`, successMessage )
				heapEvent( 'Inventory - Transfer Orders - Create - Page - Submit - Success', {
					transfer_order_id: response.transferOrder?.id ?? '',
				} )

				if ( locationState?.requestId && locationState?.selectedLineItems?.length ) {
					const transferOrderId = response.transferOrder?.id
					const allSelectedLineItemIds = locationState.selectedLineItems.map( ( li ) => li.lineItemId )

					const fullyFulfilledLineItemIds = locationState.selectedLineItems
						.filter( ( requestItem ) => {
							const match = form.formData.lineItems.find(
								( formItem ) => formItem.productId === String( requestItem.productId )
							)
							return match && match.quantityRequested >= requestItem.quantityRequested
						} )
						.map( ( li ) => li.lineItemId )

					if ( transferOrderId && allSelectedLineItemIds.length > 0 ) {
						bulkUpdateFulfillerNotes( {
							variables: {
								input: {
									demandRequestId: locationState.requestId,
									lineItemIds: allSelectedLineItemIds,
									fulfillerNotes: `TO-${transferOrderId}`,
								},
							},
						} ).catch( ( err ) => {
							// eslint-disable-next-line no-console
							console.error( 'Failed to update fulfiller notes:', err )
						} )
					}

					if ( fullyFulfilledLineItemIds.length > 0 ) {
						completeLineItems( {
							variables: {
								input: {
									demandRequestId: locationState.requestId,
									lineItemIds: fullyFulfilledLineItemIds,
								},
							},
						} ).catch( ( err ) => {
							// eslint-disable-next-line no-console
							console.error( 'Failed to auto-complete line items:', err )
						} )
					}
				}

				form.resetForm()
				setShowValidationErrors( false )
				navigate( locationState?.requestId ? `/requests/${locationState.requestId}` : `/transfer-orders/${response.transferOrder?.id}` )
			} else {
				const errorMsg = response?.message ?? 'Transfer order creation failed'
				setHasError( true )
				setErrorMessage( errorMsg )
				showError( `transferOrderError${Date.now()}`, errorMsg )
				heapEvent( 'Inventory - Transfer Orders - Create - Page - Submit - Error', {
					error: errorMsg,
				} )
			}
		} catch ( error ) {
			setHasError( true )
			const errorMsg = error instanceof Error ? error.message : 'An error occurred while creating the transfer order'
			setErrorMessage( errorMsg )
			showError( `transferOrderError${Date.now()}`, errorMsg )
			heapEvent( 'Inventory - Transfer Orders - Create - Page - Submit - Error', {
				error: errorMsg,
			} )
		} finally {
			setIsLoading( false )
		}
	}

	return (
		<PermissionsCheck
			permissionsToCheck={[ Permission.CanSubmitPartsTransferByStoreId ]}
			renderNoAccess={() => <Navigate to="/transfer-orders" replace />}
		>
			<Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				{/* Scrollable content */}
				<Box sx={{ flex: 1, overflowY: 'auto', pb: `${LAYOUT_CONSTANTS.contentBottomPadding}px` }}>
					{/* Page Header */}
					<EuiPageHeader
						pageTitle={<EuiTitle size="m"><span>Create Transfer Order</span></EuiTitle>}
						paddingSize="l"
						bottomBorder="extended"
					/>

					{/* Loading */}
					{( isLoading || mutationLoading ) && (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
							<EuiLoadingSpinner size="l" />
						</Box>
					)}

					{/* Header form section */}
					<Box sx={{ p: 3 }}>
						<Box sx={{ maxWidth: 680 }}>
							<Stack spacing={2}>
								{/* From / To store row */}
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: '1fr auto 1fr',
										alignItems: 'end',
										gap: 1,
									}}
								>
									<InventoryLocationPicker
										id="from-store-picker"
										label={<>From Store <Typography component="span" color="error">*</Typography></>}
										onChange={handleFromStoreChange}
										selectedInventoryLocation={selectedFromStore}
										selectedInventoryLocationId={!selectedFromStore && locationState?.fromInventoryId ? locationState.fromInventoryId : undefined}
										formRowProps={{
											isInvalid: showValidationErrors && form.validation.fromStoreId.isInvalid,
											error: showValidationErrors ? form.validation.fromStoreId.error : undefined,
										}}
										placeholder="Select a store"
										data-testid="from-store-picker"
									/>

									<Box sx={{ pb: 1, color: 'text.secondary', textAlign: 'center' }}>
										→
									</Box>

									<InventoryLocationPicker
										id="to-store-picker"
										label={<>To Store <Typography component="span" color="error">*</Typography></>}
										onChange={handleToStoreChange}
										selectedInventoryLocation={selectedToStore}
										selectedInventoryLocationId={!selectedToStore && locationState?.toInventoryId ? locationState.toInventoryId : undefined}
										formRowProps={{
											isInvalid: showValidationErrors && form.validation.toStoreId.isInvalid,
											error: showValidationErrors ? form.validation.toStoreId.error : undefined,
										}}
										placeholder="Select a store"
										data-testid="to-store-picker"
									/>
								</Box>

								<EuiFormRow label="Requester Note" fullWidth>
									<EuiFieldText
										fullWidth
										value={form.formData.requesterNote}
										onChange={( e ) => form.setRequesterNote( e.target.value )}
										placeholder="Optional note..."
										data-testid="requester-note-field"
									/>
								</EuiFormRow>
							</Stack>
						</Box>
					</Box>

					<Divider />

					{/* Line Items */}
					<Box sx={{ p: 3 }}>
						<TransferOrderLineItemsList
							lineItems={form.lineItems}
							onAddLineItem={form.addLineItem}
							onRemoveLineItem={form.removeLineItem}
							onUpdateLineItem={form.updateLineItem}
							hasValidationErrors={form.validation.lineItems.isInvalid}
							validationErrorMessage={form.validation.lineItems.error}
							showValidationErrors={showValidationErrors}
							branchId={selectedToStore?.branchId}
						/>
					</Box>
				</Box>

				{/* Sticky Error Panel */}
				{( hasError || mutationError ) && !isErrorPanelDismissed && (
					<Box
						sx={{
							position: 'fixed',
							bottom: LAYOUT_CONSTANTS.footerHeight,
							left: 0,
							right: 0,
							backgroundColor: 'formPanel.errorBackground',
							border: '2px solid',
							borderColor: 'formPanel.errorText',
							padding: `${LAYOUT_CONSTANTS.errorPanelPaddingVertical}px ${LAYOUT_CONSTANTS.errorPanelPaddingHorizontal}px`,
							boxShadow: muiTheme.palette.formPanel.shadow,
							zIndex: Z_INDEX.stickyErrorPanel,
						}}
					>
						<EuiFlexGroup gutterSize="m" justifyContent="spaceBetween" alignItems="center">
							<EuiFlexItem>
								<EuiText color="danger">
									<strong>Error:</strong> {errorMessage ?? mutationError?.message ?? 'An error occurred'}
								</EuiText>
							</EuiFlexItem>
							<EuiFlexItem grow={false}>
								<EuiButtonIcon
									iconType={CloseLineIcon}
									color="danger"
									aria-label="Close error message"
									onClick={() => setIsErrorPanelDismissed( true )}
									display="base"
								/>
							</EuiFlexItem>
						</EuiFlexGroup>
					</Box>
				)}

				{/* Sticky Footer */}
				<Box
					sx={{
						position: 'fixed',
						bottom: 0,
						left: 0,
						right: 0,
						backgroundColor: 'formPanel.footerBackground',
						padding: `${LAYOUT_CONSTANTS.footerPaddingVertical}px ${LAYOUT_CONSTANTS.footerPaddingHorizontal}px`,
						boxShadow: muiTheme.palette.formPanel.shadow,
						zIndex: Z_INDEX.stickyFooter,
					}}
				>
					<EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
						<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minCancelButtonWidth }}>
							<EuiButton
								color="text"
								style={{
									backgroundColor: 'transparent',
									border: `1px solid ${muiTheme.palette.common.white}`,
									color: muiTheme.palette.common.white,
								}}
								onClick={handleCancel}
								disabled={isLoading || mutationLoading}
							>
								Cancel
							</EuiButton>
						</EuiFlexItem>
						<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minSubmitButtonWidth }}>
							<EuiButton
								fill
								onClick={handleSubmit}
								isLoading={isLoading || mutationLoading}
								disabled={!form.isValid || isLoading || mutationLoading}
							>
								Create Transfer Order ({form.totalQuantity} {form.totalQuantity === 1 ? 'Item' : 'Items'})
							</EuiButton>
						</EuiFlexItem>
					</EuiFlexGroup>
				</Box>
			</Box>
		</PermissionsCheck>
	)
}
